import {useEffect,useState} from 'react';
import type {ResultCursor,ResultDetail,ResultPage,ResultPayment,Snapshot} from '@platform/contracts/x';

export function useResults(query:string,active:string,state:Snapshot,refreshMilliseconds:number){
 const [page,setPage]=useState<ResultPage>({items:[],next:null}),[cursor,setCursor]=useState<{query:string;value:ResultCursor}|null>(null);
 const [detail,setDetail]=useState<ResultDetail|null>(null),[loading,setLoading]=useState(true),[detailLoading,setDetailLoading]=useState(false);
 const [failed,setFailed]=useState(false),[detailFailed,setDetailFailed]=useState(false),[retry,setRetry]=useState(0);
 const epoch=state.drawId+(['Completed','Cancelled'].includes(state.status)?':final':'');
 const requestKey=query+epoch;
 const after=cursor?.query===requestKey?cursor.value:null;
 useEffect(()=>{
  const abort=new AbortController();setLoading(true);setFailed(false);
  const params=new URLSearchParams(query);
  if(after){params.set('before',after.at);params.set('beforeId',after.id);}else setPage({items:[],next:null});
  void fetch('/api/tombala/results?'+params,{signal:abort.signal}).then(r=>{if(!r.ok)throw Error();return r.json() as Promise<ResultPage>;}).then(data=>{
   if(abort.signal.aborted)return;
   setPage(previous=>({items:after?[...previous.items.filter(item=>!data.items.some(next=>next.drawId===item.drawId)),...data.items]:data.items,next:data.next}));setLoading(false);
  }).catch(()=>{if(!abort.signal.aborted){setFailed(true);setLoading(false);}});
  return()=>abort.abort();
 },[query,epoch,after,retry]);
 useEffect(()=>{
  const abort=new AbortController();let timer:ReturnType<typeof setTimeout>|undefined;
  setDetail(null);setDetailFailed(false);setDetailLoading(!!active);
  if(!active)return()=>abort.abort();
  let inFlight=false,current:ResultDetail|null=null;
  const refresh=async()=>{
   clearTimeout(timer);if(inFlight||abort.signal.aborted)return;inFlight=true;
   try{
    const response=await fetch('/api/tombala/results/'+encodeURIComponent(active)+(current?'/payment':''),{signal:abort.signal});
    if(!response.ok)throw Error();
    if(current){const update=await response.json() as ResultPayment;if(update.drawId!==active)throw Error();current={...current,result:{...current.result,payment:update.payment,refundedMinor:update.refundedMinor}};}
    else{current=await response.json() as ResultDetail;if(current.result.drawId!==active)throw Error();}
    if(abort.signal.aborted)return;setDetail(current);setDetailLoading(false);setDetailFailed(false);
    const r=current.result;
    if(!document.hidden&&(r.payment.pendingMinor>0||r.status==='Cancelled'&&r.refundedMinor<r.ownTotalMinor))timer=setTimeout(()=>void refresh(),refreshMilliseconds);
   }catch{if(!abort.signal.aborted){setDetailFailed(true);setDetailLoading(false);}}finally{inFlight=false;}
  };
  const visible=()=>{if(document.hidden)clearTimeout(timer);else refresh();};
  refresh();document.addEventListener('visibilitychange',visible);
  return()=>{abort.abort();clearTimeout(timer);document.removeEventListener('visibilitychange',visible);};
 },[active,retry,refreshMilliseconds]);
 return {page,detail:detail?.result.drawId===active?detail:null,loading,detailLoading,failed,detailFailed,retry:()=>setRetry(v=>v+1),more:()=>{if(page.next)setCursor({query:requestKey,value:page.next});}};
}
